import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { useLocation } from "react-router-dom";
import { AppContainer, MainContentContainer, BodyContainer, ProdButton } from '../../resources/styling/appStyle';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import { CircularProgress, Grid, Chip, Stack, IconButton, Paper, InputBase, TablePagination, Checkbox, FormControlLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import styled from 'styled-components';
import { sa, DataTable, Card, PillButton, SectionHeaderRow, SelectPill } from '../../components/superAdminUi';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Cookies from 'js-cookie';
import groupService from '../../services/api/matGroupService';
import { useCanArchive } from '../../hooks/useCanArchive';
import WondeConnectionPanel from '../../components/common/WondeConnectionPanel';
import {
  impersonate,
  ScopeType,
  getCallbackUrlForProduct,
  getHomeCallbackUrl
} from '../../utils/impersonateUtils';
import { Product, PRODUCT_DISPLAY_NAMES, EI_TIER_PRODUCT_IDS } from '../../utils/Constants';
import superAdminService from '../../services/api/superAdminService';
import { DateFormat } from '../../utils/CommonFunctions';
import BulkAddSchoolsModal from '../../components/modals/BulkAddSchoolsModal';
import { registerSchoolForGroupPath } from '../school/register-school/path';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SeatCard from '../../components/common/SeatCard';
import AccountTabs from '../../components/account/AccountTabs';
import AiUsageSection from '../../components/aiUsage/AiUsageSection';
import UserManagementSection from '../../components/userManagement/UserManagementSection';
import { ACCESS_MANAGEMENT_TAB, organisationEditLink, withoutEditUser } from '../../components/userManagement/accessManagementLinks';
import {
  archiveMatAction,
  unarchiveMatAction,
  archiveSchoolAction,
  unarchiveSchoolAction,
} from './archive/archiveActions';
import { useArchiveConfirmation } from './archive/useArchiveConfirmation';
import { activeSchoolsOf, activeSchoolCountOf } from './activeSchools';


// Presentation-only styled primitives for the group account view, keyed off the shared
// Super Admin design tokens so the header, cards and tables match the approved Figma.
const GroupTitle = styled.h2`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 20px !important;
  text-transform: capitalize;
  color: ${sa.color.text};
  margin: 0;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: ${(p) => (p.$top ? 'flex-start' : 'center')};
  flex-wrap: wrap;
  gap: 8px;
  font-family: ${sa.font.family};
  font-size: 14px;
`;

const InfoLabel = styled.span`
  color: ${sa.color.textGray};
`;

const InfoValue = styled.span`
  color: ${sa.color.text};
  font-weight: ${sa.font.bold};
`;

const SubscriptionCard = styled(Card)`
  background: #E8F6FC;
  border-color: #53B5E0;
`;

const CardHeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ArchivedBanner = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: ${sa.radius.sm};
  background: ${sa.color.groupBg};
  color: ${sa.color.text};
  font-family: ${sa.font.family};
  font-size: 14px;
  line-height: 1.5;
`;

const CardHeadTitle = styled.h3`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 16px;
  color: ${sa.color.text};
  margin: 0;
`;

const EditLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 13px;
  color: ${sa.color.teal};

  &:hover {
    text-decoration: underline;
  }
`;

const SubBody = styled.div`
  font-family: ${sa.font.family};
  font-size: 14px;
  color: ${sa.color.text};
  margin-top: 16px;
  line-height: 1.5;
`;

const Muted = styled.div`
  color: ${sa.color.textGray};
  font-size: 12px;
`;

const LicenseStart = styled.span`
  color: #e8963f;
  font-weight: ${sa.font.semibold};
`;

const LicenseEnd = styled.span`
  color: ${sa.color.teal};
  font-weight: ${sa.font.semibold};
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin: 16px 0 20px;
`;

const FilterLabel = styled.span`
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${sa.color.text};
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: ${sa.radius.sm};
  background: ${sa.color.tabInactive};
  color: ${sa.color.textGray};
  font-family: ${sa.font.family};
  font-size: 12px;
  font-weight: ${sa.font.semibold};
  text-transform: capitalize;
`;

const SortHead = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const CellText = styled.span`
  color: ${sa.color.text};
`;

const HomeButton = styled(PillButton)`
  background: #de9868;
`;

function GroupDetail() {
    const navigate = useHistory();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [adminDashboardInfo, setAdminDashboardInfo] = useState({});
    const [selectedMatDetails, setMatDetails] = useState({}); // Group details object
    
    // Get matId from query params, fallback to cookie/localStorage for backward compatibility
    const queryParams = new URLSearchParams(location.search);
    const matId = queryParams.get('matId') || Cookies.get("MatDetailId") || localStorage.getItem("SelectedMatId");
    // Arriving from another organisation's Edit user panel opens Access Management on that user.
    const editUserId = queryParams.get('editUser') || undefined;
    const openOrganisationForUser = (organisation, userId) => navigate.push(organisationEditLink(organisation, userId));
    const clearEditUser = () => {
        const next = withoutEditUser(location);
        if (next) navigate.replace(next);
    };
    
    const [impersonateLoading, setImpersonateLoading] = React.useState({});
    const canArchive = useCanArchive();
    const { requestConfirmation, dialogProps: archiveDialogProps } = useArchiveConfirmation();
    
    // User Story 6.2: Combined Surveys Table - State for Insights surveys
    const [surveys, setSurveys] = useState([]);
    const [surveysLoading, setSurveysLoading] = useState(false);
    const [surveysPage, setSurveysPage] = useState(0);
    const [surveysRowsPerPage, setSurveysRowsPerPage] = useState(10);
    const [surveysTotalCount, setSurveysTotalCount] = useState(0);
    const [surveysOrder, setSurveysOrder] = useState('desc');
    const [surveysOrderBy, setSurveysOrderBy] = useState('startDate');
    const [surveysSearchTerm, setSurveysSearchTerm] = useState('');
    const [surveysStatusFilter, setSurveysStatusFilter] = useState('all');
    const [surveysAudienceFilter, setSurveysAudienceFilter] = useState(0);
    const [surveysCheckInFilter, setSurveysCheckInFilter] = useState(false);
    const [surveysShowTesting, setSurveysShowTesting] = useState(false);

    const [bulkOpen, setBulkOpen] = React.useState(false); // bulk school upload
    const handleClose = () => {
      // Reload group details after a bulk upload completes.
      if (matId) {
        setLoading(true);
        groupService.getMatById(matId)
          .then((response) => {
            if (response.success) {
              setAdminDashboardInfo(response);
              setMatDetails(response.data);
            }
          })
          .catch(err => {})
          .finally(() => setLoading(false));
      }
    };

    // Helper to get school IDs for group-level impersonation
    const getGroupSchoolIds = () => activeSchoolsOf(selectedMatDetails).map(s => s.Id).filter(Boolean);
    useEffect(() => {
        if (!matId) {
          setLoading(false);
          return;
        }
        setLoading(true)
        groupService.getMatById(matId)      
          .then((response) => {
            if (response.success) 
            {
              setAdminDashboardInfo(response);   
              setMatDetails(response.data);
              console.log("final Responce",response.data)    
            //   loadData();
              setLoading(false);          
            }
          })
          .catch(err => {})
          .finally(() => setLoading(false));
        //   const loadData = async () => {
        //     try {
        //       await new Promise((resolve) => setTimeout(resolve, 200000));        
        //     } catch (err) {       
        //     } finally {}
        //   };
                
      }, [matId]);

    // Helper function to map status string to status number
    const getStatusNumber = (statusString) => {
        const statusMap = {
            'draft': 1,
            'published': 2,
            'live': 3,
            'running': 3,
            'closed': 4,
            'cancelled': 5,
            'canceled': 5
        };
        return statusMap[statusString?.toLowerCase()] || null;
    };

    // Load surveys with server-side filtering and pagination
    const loadSurveys = (
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        searchTermParam = '',
        statusFilterParam = null,
        showTestingSurveysParam = null,
        isCheckInFilterParam = null,
        audienceFilterParam = null
    ) => {
        if (!matId) return;
        
        setSurveysLoading(true);
        const groupMatId = parseInt(matId, 10) || 0;
        const currentStatusFilter = statusFilterParam !== null ? statusFilterParam : surveysStatusFilter;
        const currentSearchTerm =
            searchTermParam !== undefined && searchTermParam !== null
                ? String(searchTermParam).trim()
                : String(surveysSearchTerm || '').trim();
        const currentAudience =
            audienceFilterParam !== null && audienceFilterParam !== undefined
                ? audienceFilterParam
                : surveysAudienceFilter;
        
        // Map status filter to statuses array
        const statuses = [];
        if (currentStatusFilter && currentStatusFilter !== 'all' && currentStatusFilter !== '') {
            const statusNumber = getStatusNumber(currentStatusFilter);
            if (statusNumber) {
                statuses.push(statusNumber);
            }
        }
        
        // Build search params
        const searchParams = {
            matId: groupMatId,
            matSurveysOnly: true,
            pageNumber: pageNumber,
            pageSize: pageSize
        };
        
        if (currentSearchTerm && currentSearchTerm.length > 0) {
            searchParams.searchTerm = currentSearchTerm;
        }
        
        if (statuses.length > 0) {
            searchParams.statuses = statuses;
        }
        
        if (currentAudience && currentAudience > 0) {
            searchParams.audience = currentAudience;
        }
        
        // Only send check-in filter when checked (true)
        // Use parameter if provided (for immediate updates), otherwise use state
        const currentCheckInFilter = isCheckInFilterParam !== null ? isCheckInFilterParam : surveysCheckInFilter;
        if (currentCheckInFilter) {
            searchParams.isCheckInSurvey = true;
        }

        // Always pass explicit boolean for testing surveys filter
        // Backend handles filtering: true = show only testing, false = exclude testing
        // Use parameter if provided (for immediate updates), otherwise use state
        const currentShowTesting = showTestingSurveysParam !== null ? showTestingSurveysParam : surveysShowTesting;
        searchParams.showTestingSurveys = currentShowTesting;
        
        superAdminService
            .searchInsightsSurveys(searchParams)
            .then((response) => {
                if (response.success && response.data) {
                    // The Insights API returns PascalCase fields (Name, Status, Audience, SchoolName,
                    // MatName, MatId) with camelCase dates — same shape the dashboard reads. Read
                    // PascalCase first (lowercase fallback) so the rows actually populate.
                    const mappedData = response.data.map(s => ({
                        id: s.Id ?? s.id,
                        name: s.Name ?? s.name ?? '',
                        startDate: s.startDate ?? s.StartDate,
                        endDate: s.endDate ?? s.EndDate,
                        status: s.Status ?? s.status ?? '',
                        audience: s.Audience ?? s.audience ?? 0,
                        schoolName: s.SchoolName ?? s.schoolName ?? '',
                        matName: s.MatName ?? s.matName ?? '',
                        matId: s.MatId ?? s.matId ?? 0
                    }));
                    
                    setSurveys(mappedData);
                    setSurveysTotalCount(response.totalCount || 0);
                } else {
                    setSurveys([]);
                    setSurveysTotalCount(0);
                }
            })
            .catch((err) => {
                console.error('Error loading Insights surveys:', err);
                setSurveys([]);
                setSurveysTotalCount(0);
            })
            .finally(() => {
                setSurveysLoading(false);
            });
    };

    useEffect(() => {
        if (!matId) return;
        loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, '', 'all');
    }, [matId]);

    const handleSurveySearchChange = (event) => {
        const newSearchTerm = event.target.value;
        setSurveysSearchTerm(newSearchTerm);
        // Only trigger search if 3 or more characters, or if clearing the search (empty)
        if (newSearchTerm.length === 0 || newSearchTerm.length >= 3) {
            setSurveysPage(0);
            loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, newSearchTerm, surveysStatusFilter);
        }
    };

    const handleSurveyStatusFilterChange = (event) => {
        const newStatus = event.target.value;
        setSurveysStatusFilter(newStatus);
        setSurveysPage(0);
        loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, newStatus);
    };

    const handleSurveyAudienceFilterChange = (event) => {
        const newAudience = parseInt(event.target.value, 10) || 0;
        setSurveysAudienceFilter(newAudience);
        setSurveysPage(0);
        // Pass newAudience directly to avoid stale state issue (searchTerm, statusFilter, showTestingSurveys, isCheckIn, audience)
        loadSurveys(
            1,
            surveysRowsPerPage,
            surveysOrderBy,
            surveysOrder,
            surveysSearchTerm,
            surveysStatusFilter,
            null,
            null,
            newAudience
        );
    };

    const handleSurveyCheckInFilterChange = (event) => {
        const newValue = event.target.checked;
        setSurveysCheckInFilter(newValue);
        setSurveysPage(0);
        // Pass newValue directly to avoid stale state issue
        loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter, null, newValue);
    };

    const handleSurveyShowTestingChange = (event) => {
        const newValue = event.target.checked;
        setSurveysShowTesting(newValue);
        setSurveysPage(0);
        // Pass newValue directly to avoid stale state issue (showTestingSurveysParam, isCheckInFilterParam)
        loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter, newValue, null);
    };

    const handleSurveySort = (property) => {
        const isAsc = surveysOrderBy === property && surveysOrder === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setSurveysOrder(newOrder);
        setSurveysOrderBy(property);
        setSurveysPage(0);
        loadSurveys(1, surveysRowsPerPage, property, newOrder, surveysSearchTerm, surveysStatusFilter);
    };

    const handleSurveyChangePage = (event, newPage) => {
        setSurveysPage(newPage);
        loadSurveys(newPage + 1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter);
    };

    const handleSurveyChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setSurveysRowsPerPage(newRowsPerPage);
        setSurveysPage(0);
        loadSurveys(1, newRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter);
    };  

      const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      };


    const handleMatSubscriptionDetails = (id) => {   
        // Handle edit action
        console.log('Edit item with ID:', id);
        navigate.push(`/edit-subscription?matId=${id}`)
        // You can open a modal or navigate to an edit page here
    };

    
      /* Welbee Grid Properties and function starts*/
      const handleSchoolDetails = (id) => {
        // Handle edit action
        console.log('Edit item with ID:', id);
        navigate.push(`/school-detail?schoolId=${id}`)
        // You can open a modal or navigate to an edit page here
      };
    
       /* Welbee Grid Properties and function starts*/
       const handleMatDetails = (id) => {
        // Handle edit action
        console.log('Edit item with ID:', id);
        navigate.push(`/group-detail?matId=${id}`)
        // You can open a modal or navigate to an edit page here
      };
    

  // Identity stores no school or group records, so the audit trail can only name the customer a
  // super admin entered if we send the name with the impersonation. Both are already on screen
  // here; when one can't be resolved the API falls back to "<Type> <Id>".
  const auditEntityName = {
    group: () => selectedMatDetails?.Name || '',
    school: (schoolId) =>
      (selectedMatDetails?.GroupDirectors?.[0]?.MatGroup?.Schools || []).find(
        (s) => String(s?.Id) === String(schoolId)
      )?.Name || '',
  };

  const handleEDUIntellMATclick = async (MATId, _SchoolId) => {
        const callbackUrl = getCallbackUrlForProduct(Product.EDU_INTELLIGENCE);
        const buttonKey = `edu-mat-${MATId}`;
        const groupSchoolIds = getGroupSchoolIds();
        
        await impersonate({
          entityType: ScopeType.GROUP,
          entityId: MATId,
          entityName: auditEntityName.group(),
          schoolIds: groupSchoolIds,          callbackUrl,
          setLoading: (loading) => {
            setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
          },
          productName: PRODUCT_DISPLAY_NAMES[Product.EDU_INTELLIGENCE],
          productType: Product.EDU_INTELLIGENCE,
        });
      };

      const handleEDUIntellSchoolclick = async (MATId, SchoolId) => {
        const callbackUrl = getCallbackUrlForProduct(Product.EDU_INTELLIGENCE);
        const buttonKey = `edu-school-${SchoolId}`;
        
        await impersonate({
          entityType: ScopeType.SCHOOL,
          entityId: SchoolId,
          entityName: auditEntityName.school(SchoolId),
          groupId: MATId,
          callbackUrl,
          setLoading: (loading) => {
            setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
          },
          productName: PRODUCT_DISPLAY_NAMES[Product.EDU_INTELLIGENCE],
          productType: Product.EDU_INTELLIGENCE,
        });
      };
    
      const handleSchoolInsightsclick = async (MATId, SchoolId, productId = Product.STUDENT_INSIGHT) => {
        const callbackUrl = getCallbackUrlForProduct(productId);
        const buttonKey = `insights-school-${productId}-${SchoolId}`;
        
        await impersonate({
          entityType: ScopeType.SCHOOL,
          entityId: SchoolId,
          entityName: auditEntityName.school(SchoolId),
          groupId: MATId,
          callbackUrl,
          setLoading: (loading) => {
            setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
          },
          productName: PRODUCT_DISPLAY_NAMES[productId] || 'Insights',
          productType: productId,
        });
      };
      
      const handleMatInsightsclick = async (MATId, _SchoolId, productId = Product.STUDENT_INSIGHT) => {
        const callbackUrl = getCallbackUrlForProduct(productId);
        const buttonKey = `insights-mat-${productId}-${MATId}`;
        const groupSchoolIds = getGroupSchoolIds();
        
        await impersonate({
          entityType: ScopeType.GROUP,
          entityId: MATId,
          entityName: auditEntityName.group(),
          schoolIds: groupSchoolIds,          callbackUrl,
          setLoading: (loading) => {
            setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
          },
          productName: PRODUCT_DISPLAY_NAMES[productId] || 'Insights',
          productType: productId,
        });
      };
    
      const handleSchoolWelbeingclick = async (MATId, SchoolId) => {
        const callbackUrl = getCallbackUrlForProduct(Product.MAIN_APP_TOOLKIT);
        const buttonKey = `wellbeing-school-${SchoolId}`;
        
        await impersonate({
          entityType: ScopeType.SCHOOL,
          entityId: SchoolId,
          entityName: auditEntityName.school(SchoolId),
          groupId: MATId,
          callbackUrl,
          setLoading: (loading) => {
            setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
          },
          productName: PRODUCT_DISPLAY_NAMES[Product.MAIN_APP_TOOLKIT],
          productType: Product.MAIN_APP_TOOLKIT,
        });
      };
      
      const handleMatWelbeingclick = async (MATId, _SchoolId, _MATToken) => {
        const callbackUrl = getCallbackUrlForProduct(Product.MAIN_APP_TOOLKIT);
        const buttonKey = `wellbeing-mat-${MATId}`;
        const groupSchoolIds = getGroupSchoolIds();
        
        await impersonate({
          entityType: ScopeType.GROUP,
          entityId: MATId,
          entityName: auditEntityName.group(),
          schoolIds: groupSchoolIds,          callbackUrl,
          setLoading: (loading) => {
            setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
          },
          productName: PRODUCT_DISPLAY_NAMES[Product.MAIN_APP_TOOLKIT],
          productType: Product.MAIN_APP_TOOLKIT,
        });
      };
    
      const handleSchoolHomeclick = async (MATId, SchoolId) => {
        const callbackUrl = getHomeCallbackUrl(ScopeType.SCHOOL);
        const buttonKey = `home-school-${SchoolId}`;
        
        await impersonate({
          entityType: ScopeType.SCHOOL,
          entityId: SchoolId,
          entityName: auditEntityName.school(SchoolId),
          groupId: MATId,
          callbackUrl,
          setLoading: (loading) => {
            setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
          },
          productName: 'Home'
        });
      };
      
      const handleMatHomeclick = async (MATId, _SchoolId) => {
        const callbackUrl = getHomeCallbackUrl(ScopeType.GROUP);
        const buttonKey = `home-mat-${MATId}`;
        const groupSchoolIds = getGroupSchoolIds();
        
        await impersonate({
          entityType: ScopeType.GROUP,
          entityId: MATId,
          entityName: auditEntityName.group(),
          schoolIds: groupSchoolIds,          callbackUrl,
          setLoading: (loading) => {
            setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
          },
          productName: 'Home'
        });
      };
      
      // Archive-only policy: schools are never unlinked from a trust. Archiving a school
      // starts a subscription grace period and drops it from active rosters/seat counts
      // while retaining its history; unarchiving restores it with a new subscription.
      // Archiving the MAT cascades to its active schools; unarchiving it restores only the MAT.
      // The page stays put after either, since GetById still returns an archived MAT.
      const reloadGroup = () => {
        if (!matId) return;
        setLoading(true);
        groupService.getMatById(matId)
          .then((response) => {
            if (response.success) {
              setAdminDashboardInfo(response);
              setMatDetails(response.data);
            }
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      };

      const isGroupArchived = Boolean(selectedMatDetails?.IsArchived);

      const handleArchiveMatClick = () => {
        requestConfirmation(
          archiveMatAction({
            matId,
            matName: selectedMatDetails?.Name,
            activeSchoolCount: activeSchoolCountOf(selectedMatDetails),
          }),
          reloadGroup
        );
      };

      const handleUnarchiveMatClick = () => {
        requestConfirmation(unarchiveMatAction({ matId, matName: selectedMatDetails?.Name }), reloadGroup);
      };
     



  // Per-school product chips (school-level impersonation), keeping the existing handlers.
  const renderSchoolProducts = (detail) => (
    <Stack direction="row" flexWrap="wrap" rowGap={0.5} columnGap={0.5}>
      {detail.schoolProductsList && detail.schoolProductsList.length > 0 ? (
        detail.schoolProductsList
          .filter((item, index, self) => self.findIndex((i) => i.Product === item.Product) === index)
          .map((schoolProds) => (
            <React.Fragment key={schoolProds.id}>
              {schoolProds.Product === Product.MAIN_APP_TOOLKIT && (
                <ProdButton
                  label="Main App & Toolkit"
                  onClick={() => handleSchoolWelbeingclick(schoolProds.MatId, schoolProds.SchoolId)}
                  className='staff-chip'
                  size='small'
                  disabled={impersonateLoading[`wellbeing-school-${schoolProds.SchoolId}`]}
                >
                  {impersonateLoading[`wellbeing-school-${schoolProds.SchoolId}`] ? 'Loading...' : 'Main App & Toolkit'}
                </ProdButton>
              )}
              {schoolProds.Product === Product.STUDENT_INSIGHT && (
                <ProdButton
                  label="Student Insight"
                  onClick={() => handleSchoolInsightsclick(schoolProds.MatId, schoolProds.SchoolId, Product.STUDENT_INSIGHT)}
                  className='parent-chip'
                  size='small'
                  disabled={impersonateLoading[`insights-school-${Product.STUDENT_INSIGHT}-${schoolProds.SchoolId}`]}
                >
                  {impersonateLoading[`insights-school-${Product.STUDENT_INSIGHT}-${schoolProds.SchoolId}`] ? 'Loading...' : 'Student Insight'}
                </ProdButton>
              )}
              {schoolProds.Product === Product.PARENTS_INSIGHTS && (
                <ProdButton
                  label="Parents Insights"
                  onClick={() => handleSchoolInsightsclick(schoolProds.MatId, schoolProds.SchoolId, Product.PARENTS_INSIGHTS)}
                  className='insight-chip'
                  size='small'
                  disabled={impersonateLoading[`insights-school-${Product.PARENTS_INSIGHTS}-${schoolProds.SchoolId}`]}
                >
                  {impersonateLoading[`insights-school-${Product.PARENTS_INSIGHTS}-${schoolProds.SchoolId}`] ? 'Loading...' : 'Parents Insights'}
                </ProdButton>
              )}
              {schoolProds.Product === Product.STAFF_INSIGHTS && (
                <ProdButton
                  label="Staff Insights"
                  onClick={() => handleSchoolInsightsclick(schoolProds.MatId, schoolProds.SchoolId, Product.STAFF_INSIGHTS)}
                  className='student-chip'
                  size='small'
                  disabled={impersonateLoading[`insights-school-${Product.STAFF_INSIGHTS}-${schoolProds.SchoolId}`]}
                >
                  {impersonateLoading[`insights-school-${Product.STAFF_INSIGHTS}-${schoolProds.SchoolId}`] ? 'Loading...' : 'Staff Insights'}
                </ProdButton>
              )}
              {EI_TIER_PRODUCT_IDS.includes(schoolProds.Product) && (
                <ProdButton
                  label={PRODUCT_DISPLAY_NAMES[schoolProds.Product]}
                  onClick={() => handleEDUIntellSchoolclick(schoolProds.MatId, schoolProds.SchoolId)}
                  className='edu-chip'
                  size='small'
                  disabled={impersonateLoading[`edu-school-${schoolProds.SchoolId}`]}
                >
                  {impersonateLoading[`edu-school-${schoolProds.SchoolId}`] ? 'Loading...' : PRODUCT_DISPLAY_NAMES[schoolProds.Product]}
                </ProdButton>
              )}
              {schoolProds.Product === Product.PULSE && (
                <ProdButton label="Pulse" className='lparent-chip' size='small'>Pulse</ProdButton>
              )}
            </React.Fragment>
          ))
      ) : (
        <Chip label="No products available." className='learning-chip' size='small' />
      )}
    </Stack>
  );

  const renderSchoolActions = (detail) => (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
      <HomeButton
        type='button'
        onClick={() => handleSchoolHomeclick(detail.MatGroupId, detail.Id)}
        disabled={impersonateLoading[`home-school-${detail.Id}`]}
      >
        {impersonateLoading[`home-school-${detail.Id}`] ? 'Loading...' : 'Home'}
      </HomeButton>
      <PillButton type='button' onClick={() => handleSchoolDetails(detail.Id)}>Detail</PillButton>
      {canArchive && (detail.IsArchived ? (
        <PillButton
          type='button'
          $variant='danger'
          onClick={() => requestConfirmation(unarchiveSchoolAction({ schoolId: detail.Id }), reloadGroup)}
          aria-label={`Unarchive ${detail.Name}`}
        >
          Unarchive
        </PillButton>
      ) : (
        <PillButton
          type='button'
          $variant='danger'
          onClick={() => requestConfirmation(archiveSchoolAction({ schoolId: detail.Id }), reloadGroup)}
          aria-label={`Archive ${detail.Name}`}
        >
          Archive
        </PillButton>
      ))}
    </Stack>
  );

  const schoolColumns = [
    {
      key: 'name',
      label: 'School Name',
      width: '20%',
      cellStyle: { whiteSpace: 'normal' },
      render: (d) => (
        <CellText>
          {d.Name}
          {d.IsArchived && <Chip label="Archived" size="small" sx={{ ml: 1 }} />}
        </CellText>
      ),
    },
    { key: 'urn', label: 'School URN', width: '10%', render: (d) => d.WondeeSchoolUrn },
    { key: 'holder', label: 'Account Holder', width: '10%', render: (d) => d.Email || '—' },
    { key: 'product', label: 'Product', width: '40%',  cellStyle: { whiteSpace: 'normal' }, render: (d) => renderSchoolProducts(d) },
    { key: 'action', label: 'Action', width: '20%', cellStyle: { whiteSpace: 'normal' }, render: (d) => renderSchoolActions(d) },
  ];

  // Surveys table — audience chips, group name and sortable headers mirror the prior table.
  const renderAudience = (row) => {
    const audienceValue = row.audience !== undefined ? row.audience : (row.Audience !== undefined ? row.Audience : null);
    const audience = audienceValue !== null && audienceValue !== undefined
      ? (typeof audienceValue === 'string' ? parseInt(audienceValue, 10) : audienceValue)
      : null;
    return (
      <Stack direction='row' spacing={0.5}>
        {audience === 1 ? (
          <Chip label='Staff' className='staff-chip' size='small' />
        ) : audience === 2 ? (
          <Chip label='Parent' className='parent-chip' size='small' />
        ) : audience === 3 ? (
          <>
            <Chip label='Staff' className='staff-chip' size='small' />
            <Chip label='Parent' className='parent-chip' size='small' />
          </>
        ) : audience === 4 ? (
          <Chip label='Student' className='student-chip' size='small' />
        ) : audience === 5 ? (
          <>
            <Chip label='Staff' className='staff-chip' size='small' />
            <Chip label='Student' className='student-chip' size='small' />
          </>
        ) : audience === 6 ? (
          <>
            <Chip label='Parent' className='parent-chip' size='small' />
            <Chip label='Student' className='student-chip' size='small' />
          </>
        ) : audience === 7 ? (
          <>
            <Chip label='Staff' className='staff-chip' size='small' />
            <Chip label='Parent' className='parent-chip' size='small' />
            <Chip label='Student' className='student-chip' size='small' />
          </>
        ) : (
          <Chip label={audienceValue !== null && audienceValue !== undefined ? `Audience ${audienceValue}` : 'Unknown'} size='small' />
        )}
      </Stack>
    );
  };

  const surveyGroupName = (row) => (row.matId && row.matId > 0 ? (row.schoolName || '') : (row.matName || ''));

  const sortHead = (label, property) => (
    <SortHead onClick={() => handleSurveySort(property)}>
      {label}
      <SwapVertIcon style={{ fontSize: 16, opacity: surveysOrderBy === property ? 1 : 0.7 }} />
    </SortHead>
  );

  const surveyColumns = [
    { key: 'name', header: sortHead('Survey Name', 'Name'), width: '30%', cellStyle: { whiteSpace: 'normal' }, render: (r) => <CellText>{r.name || ''}</CellText> },
    { key: 'launch', header: sortHead('Launch Date', 'startDate'), render: (r) => (r.startDate ? DateFormat(r.startDate, 'MM/DD/YYYY') : '-') },
    { key: 'close', header: sortHead('Close Date', 'endDate'), render: (r) => (r.endDate ? DateFormat(r.endDate, 'MM/DD/YYYY') : '-') },
    { key: 'status', header: sortHead('Status', 'Status'), render: (r) => (r.status ? <StatusPill>{r.status}</StatusPill> : '-') },
    { key: 'audience', label: 'Audience(s)', cellStyle: { whiteSpace: 'normal' }, render: (r) => renderAudience(r) },
    { key: 'group', label: 'Group', render: (r) => surveyGroupName(r) || '-' },
  ];

  // Billing history is presentation-only placeholder data on this view; kept as-is.
  const billingRows = [
    { id: 'new', date: 'Sun Jun 14 2022', pupils: 100, from: 'New Account', price: 247, discount: '5%' },
    { id: 'renew', date: 'Sun Jun 14 2022', pupils: 100, from: 'Renew Account', price: 247, discount: '5%' },
  ];

  const billingProducts = (
    <Stack direction="row" rowGap={0.5} columnGap={0.5} flexWrap="wrap">
      <Chip label="Staff Wellbeing" className='staff-chip' size='small' />
      <Chip label="Staff Insight" className='student-chip' size='small' />
    </Stack>
  );

  const billingColumns = [
    { key: 'date', label: 'Order Date', render: (r) => r.date },
    { key: 'product', label: 'Product', cellStyle: { whiteSpace: 'normal' }, render: () => billingProducts },
    { key: 'pupils', label: 'No. of Pupil', render: (r) => r.pupils },
    { key: 'from', label: 'Generated From', render: (r) => r.from },
    { key: 'price', label: 'Total Price', render: (r) => r.price },
    { key: 'discount', label: 'Discount', render: (r) => r.discount },
    {
      key: 'invoice',
      label: 'Invoice',
      render: () => (
        <IconButton disableRipple sx={{ p: 0 }}>
          <FileDownloadOutlinedIcon />
        </IconButton>
      ),
    },
  ];

  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'live', label: 'Live' },
    { value: 'draft', label: 'Draft' },
    { value: 'closed', label: 'Closed' },
    { value: 'published', label: 'Published' },
  ];

  const audienceOptions = [
    { value: 0, label: 'All' },
    { value: 1, label: 'Staff' },
    { value: 2, label: 'Parents' },
    { value: 3, label: 'Staff & Parents' },
    { value: 4, label: 'Pupils' },
    { value: 5, label: 'Staff & Students' },
    { value: 6, label: 'Parents & Students' },
    { value: 7, label: 'Staff, Parents & Students' },
  ];

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
        {loading===true? (
                <div className="center-section-loader">
                <CircularProgress />
                </div>  
            ) : (
            <div>
                {/* Group account header */}
                <Grid container spacing={2}>
                    <Grid item xs={12} lg={8} md={12} sm={12}>
                        <Card>
                            <CardHeadRow>
                                <GroupTitle>
                                    {selectedMatDetails.Name}
                                    {isGroupArchived && <Chip label="Archived" size="small" sx={{ ml: 1 }} />}
                                </GroupTitle>
                                <HeadActions>
                                    <PillButton
                                        type='button'
                                        $variant='danger'
                                        onClick={() => navigate.push(`/edit-group?matId=${matId}`)}
                                    >
                                        Edit Group
                                    </PillButton>
                                    {canArchive && (isGroupArchived ? (
                                        <PillButton type='button' $variant='outline' onClick={handleUnarchiveMatClick} aria-label={`Unarchive ${selectedMatDetails.Name}`}>
                                            Unarchive
                                        </PillButton>
                                    ) : (
                                        <PillButton type='button' $variant='outline' onClick={handleArchiveMatClick} aria-label={`Archive ${selectedMatDetails.Name}`}>
                                            Archive
                                        </PillButton>
                                    ))}
                                </HeadActions>
                            </CardHeadRow>
                            {isGroupArchived && (
                                <ArchivedBanner role="status">
                                    This MAT is archived.
                                </ArchivedBanner>
                            )}
                            <InfoList>
                                <InfoRow>
                                    <InfoLabel>Group UID:</InfoLabel>
                                    <InfoValue>{selectedMatDetails?.MatGroupUID || selectedMatDetails?.MatGroup?.MatGroupUID || selectedMatDetails?.GroupDirectors?.[0]?.MatGroup?.MatGroupUID || 'N/A'}</InfoValue>
                                </InfoRow>
                                <InfoRow>
                                    <InfoLabel>Contact Email:</InfoLabel>
                                    <InfoValue>{selectedMatDetails?.Email || 'N/A'}</InfoValue>
                                </InfoRow>
                                <InfoRow>
                                    <InfoLabel>Number of Staff:</InfoLabel>
                                    <InfoValue>{selectedMatDetails?.TotalMatStaff || 0}</InfoValue>
                                </InfoRow>
                                <InfoRow>
                                    <InfoLabel>Number of Pupils:</InfoLabel>
                                    <InfoValue>{selectedMatDetails?.TotalMatPupils || 0}</InfoValue>
                                </InfoRow>
                                <InfoRow>
                                    <InfoLabel>Customer Type:</InfoLabel>
                                    <InfoValue>
                                        {selectedMatDetails?.CustomerType
                                            ? (typeof selectedMatDetails.CustomerType === 'string'
                                                ? (() => {
                                                    try {
                                                        const parsed = JSON.parse(selectedMatDetails.CustomerType);
                                                        return Array.isArray(parsed) ? parsed.join(', ') : parsed;
                                                    } catch (e) {
                                                        return selectedMatDetails.CustomerType;
                                                    }
                                                })()
                                                : (Array.isArray(selectedMatDetails.CustomerType)
                                                    ? selectedMatDetails.CustomerType.join(', ')
                                                    : selectedMatDetails.CustomerType))
                                            : 'N/A'}
                                    </InfoValue>
                                </InfoRow>
                                <InfoRow>
                                    <InfoLabel>Wonde Sync Status:</InfoLabel>
                                    <WondeConnectionPanel matId={matId} isMatLevel={true} />
                                </InfoRow>
                                <InfoRow $top>
                                    <InfoLabel>Subscribe Products:</InfoLabel>
                                    <Stack direction='row' flexWrap="wrap" rowGap={0.5} columnGap={0.5}>
                                        {((selectedMatDetails.MatSchoolProductsList || selectedMatDetails.MatSchoolProducts) || []).map((marProducts) => (
                                            <React.Fragment key={marProducts.id}>
                                                {marProducts.Product === Product.MAIN_APP_TOOLKIT && (
                                                    <ProdButton
                                                      label="Main App & Toolkit"
                                                      onClick={() => handleMatWelbeingclick(marProducts.MatId,marProducts.SchoolId,selectedMatDetails.GroupDirectors?.[0]?.UserId)}
                                                      className='staff-chip'                                                      
                                                      disabled={impersonateLoading[`wellbeing-mat-${marProducts.MatId}`]}
                                                    >
                                                      {impersonateLoading[`wellbeing-mat-${marProducts.MatId}`] ? 'Loading...' : 'Main App & Toolkit'}
                                                    </ProdButton>
                                                )}
                                                {marProducts.Product === Product.STUDENT_INSIGHT && (
                                                    <ProdButton
                                                      label="Student Insight"
                                                      onClick={() => handleMatInsightsclick(marProducts.MatId,marProducts.SchoolId, Product.STUDENT_INSIGHT)}
                                                      className='parent-chip'
                                                      size='small'
                                                      disabled={impersonateLoading[`insights-mat-${Product.STUDENT_INSIGHT}-${marProducts.MatId}`]}
                                                    >
                                                      {impersonateLoading[`insights-mat-${Product.STUDENT_INSIGHT}-${marProducts.MatId}`] ? 'Loading...' : 'Student Insight'}
                                                    </ProdButton>
                                                )}
                                                {marProducts.Product === Product.PARENTS_INSIGHTS && (
                                                    <ProdButton
                                                      label="Parents Insights"
                                                      onClick={() => handleMatInsightsclick(marProducts.MatId,marProducts.SchoolId, Product.PARENTS_INSIGHTS)}
                                                      className='insight-chip'
                                                      size='small'
                                                      disabled={impersonateLoading[`insights-mat-${Product.PARENTS_INSIGHTS}-${marProducts.MatId}`]}
                                                    >
                                                      {impersonateLoading[`insights-mat-${Product.PARENTS_INSIGHTS}-${marProducts.MatId}`] ? 'Loading...' : 'Parents Insights'}
                                                    </ProdButton>
                                                )}
                                                {marProducts.Product === Product.STAFF_INSIGHTS && (
                                                    <ProdButton
                                                      label="Staff Insights"
                                                      onClick={() => handleMatInsightsclick(marProducts.MatId,marProducts.SchoolId, Product.STAFF_INSIGHTS)}
                                                      className='student-chip'
                                                      size='small'
                                                      disabled={impersonateLoading[`insights-mat-${Product.STAFF_INSIGHTS}-${marProducts.MatId}`]}
                                                    >
                                                      {impersonateLoading[`insights-mat-${Product.STAFF_INSIGHTS}-${marProducts.MatId}`] ? 'Loading...' : 'Staff Insights'}
                                                    </ProdButton>
                                                )}
                                                {EI_TIER_PRODUCT_IDS.includes(marProducts.Product) && (
                                                    <ProdButton
                                                      label={PRODUCT_DISPLAY_NAMES[marProducts.Product]}
                                                      onClick={() => handleEDUIntellMATclick(marProducts.MatId,marProducts.SchoolId)}
                                                      className='edu-chip'
                                                      size='small'
                                                      disabled={impersonateLoading[`edu-mat-${marProducts.MatId}`]}
                                                    >
                                                      {impersonateLoading[`edu-mat-${marProducts.MatId}`] ? 'Loading...' : PRODUCT_DISPLAY_NAMES[marProducts.Product]}
                                                    </ProdButton>
                                                )}
                                                {marProducts.Product === Product.PULSE && (
                                                    <Chip label="Pulse" className='lparent-chip' size='small'/>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </Stack>
                                </InfoRow>
                            </InfoList>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={4} md={12} sm={12}>
                        <Stack spacing={1.5}>
                            <SubscriptionCard>
                                <CardHeadRow>
                                    <CardHeadTitle>Subscription Detail</CardHeadTitle>
                                    <EditLink type='button' onClick={() => handleMatSubscriptionDetails(selectedMatDetails.Id)}>Edit</EditLink>
                                </CardHeadRow>
                                <SubBody>
                                    <div style={{ fontWeight: sa.font.bold }}>Subscription Term : {selectedMatDetails.MatSubscription?.SubscriptionTerm || 0} years</div>
                                    <Muted>(Billed yearly)</Muted>
                                    <div style={{ fontWeight: sa.font.bold, marginTop: '12px' }}>Subscription Cost</div>
                                    {selectedMatDetails.MatSubscription?.SubscriptionTerm === 1 ? (
                                        <div style={{ marginTop: '4px' }}><strong>Year 1 : £{parseInt(selectedMatDetails.MatSubscription?.Year1Cost || 0)+((parseInt(selectedMatDetails.MatSubscription?.Year1Cost || 0) )*(parseInt(selectedMatDetails.MatSubscription?.VatPercentApplied || 0)/100))}</strong> ({selectedMatDetails.MatSubscription?.VatPercentApplied || 0}% VAT Applied)</div>
                                    ) : selectedMatDetails.MatSubscription?.SubscriptionTerm === 2 ? (
                                        <>
                                        <div style={{ marginTop: '4px' }}><strong>Year 1 : £{parseInt(selectedMatDetails.MatSubscription?.Year1Cost || 0)+((parseInt(selectedMatDetails.MatSubscription?.Year1Cost || 0) )*(parseInt(selectedMatDetails.MatSubscription?.VatPercentApplied || 0)/100))}</strong> ({selectedMatDetails.MatSubscription?.VatPercentApplied || 0}% VAT Applied)</div>
                                        <div style={{ marginTop: '4px' }}><strong>Year 2 : £{parseInt(selectedMatDetails.MatSubscription?.Year2Cost || 0)+((parseInt(selectedMatDetails.MatSubscription?.Year2Cost || 0) )*(parseInt(selectedMatDetails.MatSubscription?.VatPercentApplied || 0)/100))}</strong> ({selectedMatDetails.MatSubscription?.VatPercentApplied || 0}% VAT Applied)</div>
                                        </>
                                    ) : selectedMatDetails.MatSubscription?.SubscriptionTerm === 3 ? (
                                        <>
                                        <div style={{ marginTop: '4px' }}><strong>Year 1 : £{parseInt(selectedMatDetails.MatSubscription?.Year1Cost || 0)+((parseInt(selectedMatDetails.MatSubscription?.Year1Cost || 0) )*(parseInt(selectedMatDetails.MatSubscription?.VatPercentApplied || 0)/100))}</strong> ({selectedMatDetails.MatSubscription?.VatPercentApplied || 0}% VAT Applied)</div>
                                        <div style={{ marginTop: '4px' }}><strong>Year 2 : £{parseInt(selectedMatDetails.MatSubscription?.Year2Cost || 0)+((parseInt(selectedMatDetails.MatSubscription?.Year2Cost || 0) )*(parseInt(selectedMatDetails.MatSubscription?.VatPercentApplied || 0)/100))}</strong> ({selectedMatDetails.MatSubscription?.VatPercentApplied || 0}% VAT Applied)</div>
                                        <div style={{ marginTop: '4px' }}><strong>Year 3 : £{parseInt(selectedMatDetails.MatSubscription?.Year3Cost || 0)+((parseInt(selectedMatDetails.MatSubscription?.Year3Cost || 0) )*(parseInt(selectedMatDetails.MatSubscription?.VatPercentApplied || 0)/100))}</strong> ({selectedMatDetails.MatSubscription?.VatPercentApplied || 0}% VAT Applied)</div>
                                        </>
                                    ) : (
                                        <div style={{ marginTop: '4px' }}>£0</div>
                                    )}
                                    <div className="mt-12">Your License runs from <LicenseStart>{selectedMatDetails.MatSubscription?.StartDate ? formatDate(selectedMatDetails.MatSubscription.StartDate) : 'N/A'}</LicenseStart> - <LicenseEnd>{selectedMatDetails.MatSubscription?.EndDate ? formatDate(selectedMatDetails.MatSubscription.EndDate) : 'N/A'}</LicenseEnd></div>
                                </SubBody>
                            </SubscriptionCard>
                            <SeatCard
                                entityType="group"
                                entityId={selectedMatDetails.Id}
                                assignedSeats={selectedMatDetails.AssignedSeats}
                            />
                        </Stack>
                    </Grid>
                </Grid>

                {/* Account views: AI Usage takes precedence, with registered
                    schools, surveys and billing history relocated under tabs. */}
                <AccountTabs
                    idBase='group-account'
                    defaultTabKey={queryParams.get('tab') === ACCESS_MANAGEMENT_TAB ? ACCESS_MANAGEMENT_TAB : 'ai-usage'}
                    tabs={[
                        {
                            key: 'ai-usage',
                            label: 'AI Usage & Credits',
                            content: <AiUsageSection scope='group' groupId={matId} />,
                        },
                        {
                            key: 'user-management',
                            label: 'Access Management',
                            // Identity only knows scope ids; resolve school names from the group's
                            // own schools (already loaded for the Registered Schools tab).
                            content: (
                                <UserManagementSection
                                    scope='group'
                                    groupId={matId}
                                    scopeName={selectedMatDetails?.Name}
                                    editUserId={editUserId}
                                    onEditUserClosed={clearEditUser}
                                    onOpenOrganisation={openOrganisationForUser}
                                    schoolNamesById={(
                                        selectedMatDetails?.GroupDirectors?.[0]?.MatGroup?.Schools || []
                                    ).reduce((map, school) => {
                                        if (school?.Id != null) map[school.Id] = school.Name;
                                        return map;
                                    }, {})}
                                />
                            ),
                        },
                        {
                            key: 'registered-schools',
                            label: 'Registered Schools',
                            content: (
                                <>
                {/* Registered Schools */}
                <Grid container mt={1}>
                    <Grid item xs={12} lg={12} md={12} sm={12}>
                        <Card>
                            <SectionHeaderRow title='Registered Schools'>
                                <PillButton type='button' $variant='outline' onClick={() => setBulkOpen(true)}>Bulk Add</PillButton>
                                <PillButton type='button' $variant='danger' onClick={() => navigate.push(registerSchoolForGroupPath(matId))}>Add School</PillButton>
                            </SectionHeaderRow>
                            <div style={{ marginTop: sa.space.section }}>
                                <DataTable
                                    columns={schoolColumns}
                                    rows={selectedMatDetails.GroupDirectors?.[0]?.MatGroup?.Schools || []}
                                    getRowKey={(r) => r.Id}
                                    pageSize={10}                                  
                                    empty='No schools registered for this group.'
                                />
                            </div>
                        </Card>
                    </Grid>
                </Grid>
                                </>
                            ),
                        },
                        {
                            key: 'survey-info',
                            label: 'Survey Info',
                            content: (
                                <>
                {/* Surveys */}
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12}>
                        <Card>
                            <SectionHeaderRow title='Surveys' />
                            <FilterBar>
                                <Paper
                                    component="form"
                                    elevation={0}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: 280,
                                        border: `1px solid ${sa.color.stroke}`,
                                        borderRadius: sa.radius.sm,
                                        px: 1,
                                    }}
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (surveysSearchTerm.length === 0 || surveysSearchTerm.length >= 3) {
                                            setSurveysPage(0);
                                            loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter);
                                        }
                                    }}
                                >
                                    <SearchIcon sx={{ color: sa.color.textGray, fontSize: 20 }} />
                                    <InputBase
                                        sx={{ ml: 1, flex: 1, fontSize: 13 }}
                                        placeholder="Search surveys..."
                                        value={surveysSearchTerm}
                                        onChange={handleSurveySearchChange}
                                        inputProps={{ 'aria-label': 'search surveys' }}
                                    />
                                </Paper>
                                <FilterLabel>Status</FilterLabel>
                                <SelectPill
                                    value={surveysStatusFilter}
                                    onChange={(v) => handleSurveyStatusFilterChange({ target: { value: v } })}
                                    options={statusOptions}
                                    size='sm'
                                    ariaLabel='Status'
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={surveysShowTesting}
                                            onChange={handleSurveyShowTestingChange}
                                            sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                        />
                                    }
                                    label="Show Testing Surveys"
                                />
                                <FilterLabel>Audience</FilterLabel>
                                <SelectPill
                                    value={surveysAudienceFilter}
                                    onChange={(v) => handleSurveyAudienceFilterChange({ target: { value: v } })}
                                    options={audienceOptions}
                                    size='sm'
                                    ariaLabel='Audience'
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={surveysCheckInFilter}
                                            onChange={handleSurveyCheckInFilterChange}
                                            sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                        />
                                    }
                                    label="Check-in Surveys Only"
                                />
                            </FilterBar>
                            {surveysLoading && (
                                <div className="d-flex justify-center align-center p-20">
                                    <CircularProgress size={24} />
                                </div>
                            )}
                            <DataTable
                                columns={surveyColumns}
                                rows={surveys}
                                getRowKey={(r) => r.id}
                                pageSize={10}
                                size='sm'
                                empty='No surveys found for this group.'
                            />
                            {/* <TablePagination
                                rowsPerPageOptions={[10, 20, 30]}
                                component='div'
                                count={surveysTotalCount}
                                rowsPerPage={surveysRowsPerPage}
                                page={surveysPage}
                                onPageChange={handleSurveyChangePage}
                                onRowsPerPageChange={handleSurveyChangeRowsPerPage}
                            /> */}
                        </Card>
                    </Grid>
                </Grid>

                {/* Billing History */}
                <Grid container mt={3}>
                    <Grid item xs={12} lg={12} md={12} sm={12}>
                        <Card>
                            <SectionHeaderRow title='Billing History' />
                            <div style={{ marginTop: sa.space.section }}>
                                <DataTable
                                    columns={billingColumns}
                                    rows={billingRows}
                                    pageSize={10}
                                    size='sm'
                                    getRowKey={(r) => r.id}
                                />
                            </div>
                        </Card>
                    </Grid>
                </Grid>
                                </>
                            ),
                        },
                    ]}
                />
            </div>
            )}

        {/* Bulk Add Schools Modal */}
        <BulkAddSchoolsModal
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onCompleted={handleClose}
          matId={matId}
          groupName={selectedMatDetails?.Name}
        />

        <ConfirmDialog {...archiveDialogProps} />
        
        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}

export default withRouter(GroupDetail);
